import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import {
  ArrowUpRight,
  ArrowRight,
  CheckCircle2,
  Play,
  X,
  Search,
  Filter,
  Video,
  Send,
  Eye,
  MessageSquare,
  CalendarCheck,
  Smartphone,
  Zap,
  Package,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";

import { usePageTitle } from "../lib/usePageTitle";
import ScrollProgress from "../components/ScrollProgress";
import PremiumBackdrop from "../components/PremiumBackdrop";
import {
  Seo,
  organizationSchema,
  serviceSchema,
  faqPageSchema,
  breadcrumbSchema,
} from "../components/seo/Seo";

const EASE = [0.16, 1, 0.3, 1];
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND}/api`;

const ENTITY = {
  name: "Get Virtual Growth",
  url: "https://getvirtualgrowth.com",
  legalName: "Get Virtual Growth",
  founder: "Krishna S.",
};
const SERVICE = {
  name: "Personalised Video Customer Acquisition",
  description:
    "Fully managed personalised video outreach for founder-led firms. Sourcing, ICP cleaning, script, recording, delivery, open-rate management, reply handling and meeting booking - end to end.",
};

/* ================================================================== */
/*  FAQ - visible + JSON-LD                                            */
/* ================================================================== */
const CORE_FAQS = [
  {
    q: "How is this different from hiring an SDR or an agency?",
    a: "An SDR sends templates. An agency writes copy. Get Virtual Growth ships a personalised video to every named prospect - their name in the thumbnail, their firm in the opening line - and manages open rate, follow-up and meeting booking end to end. You show up on the call.",
  },
  {
    q: "What if my prospects think it is AI or a stunt?",
    a: "Every video references something publicly true about the prospect or their firm that no AI would guess. It is a real face, real voice, real thought - just produced at scale.",
  },
  {
    q: "How fast do we start?",
    a: "Within 48 hours of signing, a dedicated Get Virtual Growth team is assigned to your firm and begins building your ICP list, script and first batch of videos. Nothing is off the shelf. Everything is built for your brand.",
  },
  {
    q: "How many meetings can we realistically expect?",
    a: "Our system is engineered to book ten to thirty qualified meetings per month at Foundation, twenty to sixty at Growth, and fifty-plus at Omnichannel. Numbers scale with list quality, offer strength and category. We share private benchmarks on the strategy call.",
  },
  {
    q: "Does the founder need to be on every video?",
    a: "Not necessarily. You have three options. One: the founder records a short script once and we handle personalisation and delivery. Two: a trusted team member (a client success lead, a partner, an SDR with presence) becomes the face of your outbound. Three: on the Omnichannel tier only, you can use the Krishna twin avatar - a licensed AI-driven likeness of our founder that carries the same weight as a founder-led video, useful when your reputation or regulatory posture keeps you off camera.",
  },
  {
    q: "How do you handle compliance and privacy?",
    a: "Get Virtual Growth serves founder-led firms across the US, UK, Australia, New Zealand, the GCC and beyond. Every campaign is built around the privacy laws, communication norms and regulatory posture of your country and category - CAN-SPAM in the US, UK GDPR and PECR, Australia's Spam Act, New Zealand's Unsolicited Electronic Messages Act, and the GCC's national data protection frameworks. Messaging stays professional, honours opt-outs the first time asked, and is reviewed for anything that could touch financial, legal or medical advice. Your brand and ours only grow when they stay clean.",
  },
];

/* ================================================================== */
/*  Booking Modal                                                       */
/* ================================================================== */
function CallModal({ open, onClose, onSubmitted, preselectedTier }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [submittedEmail, setSubmittedEmail] = useState("");

  useEffect(() => {
    if (!open) {
      setDone(false);
      setName("");
      setEmail("");
      setNote("");
      setSubmittedEmail("");
    }
  }, [open]);

  useEffect(() => {
    if (open && preselectedTier) {
      setNote(`Interested in ${preselectedTier}. `);
    }
  }, [open, preselectedTier]);

  const canSubmit =
    name.trim().length >= 2 &&
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()) &&
    !submitting;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/founder-questions`, {
        question: `Growth Partnership call request. ${note.trim() || "No additional note."}`,
        author_name: name.trim(),
        author_email: email.trim(),
      });
      setSubmittedEmail(email.trim());
      setDone(true);
      onSubmitted?.();
      toast.success("Received. Krishna will reach out within one business day.");
    } catch {
      toast.error("Something did not send. Try again in a minute.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      data-testid="gp-modal"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        data-testid="gp-modal-backdrop"
      />
      <motion.div
        initial={{ opacity: 0, y: 12, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.35, ease: EASE }}
        className="relative w-full max-w-md rounded-3xl border border-brass/30 bg-[#0a0a0a] p-7 shadow-2xl"
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 text-ink-muted hover:text-brass transition-colors"
          data-testid="gp-modal-close"
        >
          <X className="h-4 w-4" />
        </button>

        {!done ? (
          <>
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
              Strategy Call
            </p>
            <h3 className="mt-3 font-serif text-3xl leading-tight text-ink">
              Thirty minutes with Krishna.
            </h3>
            <p className="mt-3 font-sans text-sm text-ink-muted leading-relaxed">
              No deck. Bring your ICP and your last outbound sequence. You leave with a plan.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-3">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value.slice(0, 80))}
                data-testid="gp-modal-name"
                className="lux-field lux-field--pad-sm w-full rounded-full"
                placeholder="Full name"
                autoComplete="name"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value.slice(0, 200))}
                data-testid="gp-modal-email"
                className="lux-field lux-field--pad-sm w-full rounded-full"
                placeholder="you@company.com"
                autoComplete="email"
              />
              <textarea
                value={note}
                onChange={(e) => setNote(e.target.value.slice(0, 400))}
                data-testid="gp-modal-note"
                rows={3}
                className="lux-field lux-field--pad-lg w-full rounded-2xl resize-none"
                placeholder="What do you sell, and who is your ideal client?"
              />
              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="gp-modal-submit"
                className="mt-2 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3.5 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? "Sending..." : "Send"}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </form>
          </>
        ) : (
          <div data-testid="gp-modal-done" className="py-2">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-brass" />
              <h3 className="font-serif text-2xl leading-tight text-ink">
                I have your intent.
              </h3>
            </div>
            <p className="mt-3 font-sans text-sm text-ink-muted leading-relaxed">
              Krishna will reach {submittedEmail} within one business day. Book time on his calendar or reply to his email.
            </p>
            <Link
              to={`/strategy-brief?e=${encodeURIComponent(submittedEmail)}`}
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500"
              data-testid="gp-modal-brief-cta"
            >
              Fill the strategy brief
              <ArrowUpRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        )}
      </motion.div>
    </div>
  );
}

/* ================================================================== */
/*  Twin Pitch card - silent loop, click reveals full pitch in-place    */
/*  Mirrors the home page TwinVideoCard pattern - no lightbox, no       */
/*  download menu, no picture-in-picture.                               */
/* ================================================================== */
function PitchCard() {
  const [playing, setPlaying] = React.useState(false);
  const pitchRef = React.useRef(null);

  const startPitch = () => {
    setPlaying(true);
    setTimeout(() => {
      const v = pitchRef.current;
      if (!v) return;
      v.muted = false;
      v.currentTime = 0;
      v.play().catch(() => {
        v.muted = true;
        v.play().catch(() => {});
      });
    }, 40);
  };

  return (
    <div
      data-testid="gp-hero-video-card"
      className="relative block w-full rounded-2xl overflow-hidden border border-brass/40 group"
      style={{
        aspectRatio: "832 / 1088",
        background: "linear-gradient(180deg, #14141A 0%, #0A0A0A 100%)",
        boxShadow: "0 30px 80px -30px rgba(201,169,97,0.35)",
      }}
    >
      {/* Silent looping preview */}
      <video
        className="absolute inset-0 w-full h-full object-cover"
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        poster="/video/krishna-twin-poster.jpg"
        aria-hidden={playing}
        style={{ opacity: playing ? 0 : 1, transition: "opacity 400ms ease" }}
        data-testid="gp-hero-loop"
      >
        <source src="/video/krishna-twin-loop.webm" type="video/webm" />
        <source src="/video/krishna-twin-loop.mp4" type="video/mp4" />
      </video>

      {/* Full pitch with audio - reveals in the same frame */}
      <video
        ref={pitchRef}
        controls={playing}
        controlsList="nodownload noplaybackrate"
        disablePictureInPicture
        onContextMenu={(e) => e.preventDefault()}
        playsInline
        preload="none"
        poster="/video/krishna-twin-poster.jpg"
        className="absolute inset-0 w-full h-full object-cover"
        style={{
          opacity: playing ? 1 : 0,
          transition: "opacity 400ms ease",
          pointerEvents: playing ? "auto" : "none",
        }}
        data-testid="gp-hero-pitch"
      >
        <source src="/video/krishna-twin-pitch.mp4" type="video/mp4" />
      </video>

      {/* Play overlay - visible until user starts the pitch */}
      {!playing && (
        <button
          type="button"
          onClick={startPitch}
          aria-label="Play the full pitch"
          data-testid="gp-hero-play"
          className="absolute inset-0 w-full h-full flex items-end justify-between p-4 cursor-pointer group/play"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.7) 100%)",
          }}
        >
          <span className="inline-flex items-center gap-2 rounded-full bg-black/60 border border-brass/40 px-3 py-1.5 text-[10px] uppercase tracking-mega text-brass backdrop-blur-md">
            <span className="h-1.5 w-1.5 rounded-full bg-brass animate-pulse" />
            Live preview
          </span>
          <span className="inline-flex items-center gap-2 rounded-full bg-brass text-bg px-3 py-1.5 text-[11px] font-semibold group-hover/play:-translate-y-0.5 transition-transform">
            <Play className="h-3.5 w-3.5" />
            Watch full pitch
          </span>
        </button>
      )}
    </div>
  );
}

/* ================================================================== */
/*  Mobile Sticky CTA                                                   */
/* ================================================================== */
function MobileStickyCta({ onBook, hide }) {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => setVisible(window.scrollY > 500);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  if (hide) return null;
  return (
    <motion.div
      initial={false}
      animate={{
        y: visible ? 0 : 120,
        opacity: visible ? 1 : 0,
      }}
      transition={{ duration: 0.4, ease: EASE }}
      className="fixed bottom-4 inset-x-4 z-40 md:hidden"
      data-testid="gp-sticky-cta"
    >
      <button
        type="button"
        onClick={onBook}
        className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brass text-bg px-6 py-4 font-sans text-[11px] uppercase tracking-mega font-semibold shadow-2xl"
        data-testid="gp-sticky-book"
      >
        Start with a Pilot
        <ArrowUpRight className="h-3.5 w-3.5" />
      </button>
    </motion.div>
  );
}

/* ================================================================== */
/*  Section - Hero                                                      */
/* ================================================================== */
function Hero({ onBook }) {
  return (
    <section
      className="relative pt-28 pb-16 md:pt-32 md:pb-24 px-6 md:px-10 lg:px-16 overflow-hidden"
      data-testid="gp-hero"
    >
      <div className="max-w-7xl mx-auto">
        <p
          className="font-sans text-[10px] uppercase tracking-mega text-brass"
          data-testid="gp-hero-eyebrow"
        >
          Get Virtual Growth . Managed Video Customer Acquisition
        </p>

        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          <div className="lg:col-span-7">
            <motion.h1
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="font-serif text-ink leading-[1.02] tracking-tight"
              style={{ fontSize: "clamp(2.4rem, 5.6vw, 4.6rem)" }}
              data-testid="gp-hero-headline"
            >
              Meetings on your calendar.<br className="hidden md:inline" />{" "}
              <span className="text-brass italic">Named prospects.</span>{" "}
              Zero cold work from you.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
              className="mt-6 font-sans text-ink-muted leading-relaxed max-w-xl"
              style={{ fontSize: "clamp(1rem, 1.4vw, 1.15rem)" }}
              data-testid="gp-hero-subhead"
            >
              We source your ideal buyers, record a video for each one by name,
              send it, manage replies, and drop booked meetings on your calendar.
              Fully managed by Get Virtual Growth. You show up on the call.
            </motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.3, ease: EASE }}
              className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-2 font-sans text-[11px] uppercase tracking-mega text-ink-muted"
              data-testid="gp-hero-microproof"
            >
              <span className="inline-flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-brass" />
                48-hour start
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-brass" />
                Founder led
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-brass" />
                Runs from your phone
              </span>
              <span className="inline-flex items-center gap-2">
                <span className="h-1 w-1 rounded-full bg-brass" />
                Pilot from $2,000
              </span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.4, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <button
                type="button"
                onClick={onBook}
                data-testid="gp-hero-book"
                className="inline-flex items-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3.5 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500"
              >
                Start with a Pilot
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.2, ease: EASE }}
            className="lg:col-span-5"
          >
            <PitchCard />
            <p className="mt-3 font-sans text-[10px] uppercase tracking-mega text-ink-muted text-right">
              This is the exact video your prospects will receive.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - What we do (7-step chain)                                 */
/* ================================================================== */
function WhatWeDo() {
  const steps = [
    { icon: Search, title: "Source your buyers", body: "We build a fresh list of named decision-makers who match your ICP. LinkedIn, industry lists, private databases." },
    { icon: Filter, title: "Clean the ICP", body: "We strip out lookalikes, wrong-title people and old data. Only true fits move forward." },
    { icon: Video, title: "Record the video", body: "One short video per prospect. Their name in the thumbnail. Their firm in the first line. Real face, real voice." },
    { icon: Send, title: "Send it", body: "Delivered from your identity, engineered to land in the inbox not spam." },
    { icon: Eye, title: "Manage open rate", body: "Subject lines, send windows and follow-up cadence tuned weekly against real-time signal." },
    { icon: MessageSquare, title: "Handle replies", body: "Every reply gets a human, on-brand response inside two hours during business days." },
    { icon: CalendarCheck, title: "Book the meeting", body: "Confirmed meetings dropped on your calendar with a briefing note. You just show up." },
  ];
  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10"
      data-testid="gp-what"
    >
      <div className="max-w-7xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          What . the service in seven moves
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight max-w-3xl"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-what-title"
        >
          Everything a growth team would do.{" "}
          <span className="text-brass italic">Done for you.</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {steps.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
              className="relative rounded-2xl border border-brass/20 bg-black/40 p-6 hover:border-brass/45 transition-colors duration-500"
              data-testid={`gp-what-step-${i}`}
            >
              <div className="flex items-center gap-3">
                <span className="h-9 w-9 rounded-full bg-brass/10 border border-brass/40 grid place-items-center text-brass">
                  <s.icon className="h-4 w-4" />
                </span>
                <span className="font-serif italic text-brass/70 text-xs">
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <h3 className="mt-4 font-serif text-xl text-ink leading-tight">
                {s.title}
              </h3>
              <p className="mt-2 font-sans text-sm text-ink-muted leading-relaxed">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - When / Where / How                                        */
/* ================================================================== */
function WhenWhereHow() {
  const cards = [
    {
      k: "When",
      icon: Zap,
      title: "48 hours or less",
      body: "The moment you decide, a dedicated Get Virtual Growth team is assigned to your firm and starts building your ICP list, script and first batch of videos within 48 hours. The sooner you decide, the sooner your brand starts compounding inside your buyers' inboxes.",
    },
    {
      k: "Where",
      icon: Smartphone,
      title: "Straight from your phone",
      body: "Log in from your phone. See progress live. Keep your calendar clean. Show up to booked meetings with a briefing note already in your inbox.",
    },
    {
      k: "How",
      icon: Package,
      title: "Pick a package. We do the rest.",
      body: "Choose Pilot, Foundation, Growth or Omnichannel. Get Virtual Growth handles sourcing, cleaning, video, sending, replies and booking. You show up on calls.",
    },
  ];
  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-black"
      data-testid="gp-when-where-how"
    >
      <div className="max-w-7xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          When . Where . How
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight max-w-3xl"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-wwh-title"
        >
          Zero setup on your side.{" "}
          <span className="text-brass italic">You keep building the business.</span>
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
          {cards.map((c, i) => (
            <motion.div
              key={c.k}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.08, ease: EASE }}
              className="rounded-2xl border border-brass/25 bg-[#0a0a0a] p-7"
              data-testid={`gp-wwh-card-${c.k.toLowerCase()}`}
            >
              <div className="flex items-center gap-3">
                <span className="h-10 w-10 rounded-full bg-brass/10 border border-brass/50 grid place-items-center text-brass">
                  <c.icon className="h-4 w-4" />
                </span>
                <span className="font-sans text-[10px] uppercase tracking-mega text-brass">
                  {c.k}
                </span>
              </div>
              <h3 className="mt-5 font-serif text-2xl text-ink leading-tight">
                {c.title}
              </h3>
              <p className="mt-3 font-sans text-sm text-ink-muted leading-relaxed">
                {c.body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - First 30 Days (what actually happens after you sign)     */
/* ================================================================== */
function FirstThirtyDays() {
  const phases = [
    {
      window: "Day 1 - 2",
      icon: Zap,
      title: "You decide. We assemble your team.",
      body: "A dedicated Get Virtual Growth pod is assigned to your firm. Kickoff call locked. ICP intake document shared. NDAs and access sorted in the same 48 hours.",
    },
    {
      window: "Day 3 - 7",
      icon: Search,
      title: "We build your list and script.",
      body: "Your ICP is sourced, cleaned and validated against real signals. Your first video script is drafted around your voice, offer and objections. Wardrobe, framing and delivery reviewed before a single camera rolls.",
    },
    {
      window: "Day 8 - 14",
      icon: Send,
      title: "First batch ships. Reply desk goes live.",
      body: "Your first named-prospect videos are recorded, personalised and delivered. Human replies land inside two hours. Open rate is tuned daily against real inbox signal.",
    },
    {
      window: "Day 15 - 30",
      icon: CalendarCheck,
      title: "Meetings on your calendar. Cadence in motion.",
      body: "First qualified meetings drop on your calendar with a briefing note. Weekly digest hits your inbox every Monday. The engine starts compounding - the same buyers who did not book this month see you again next month, warmer.",
    },
  ];

  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10"
      data-testid="gp-first-30"
    >
      <div className="max-w-7xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          Your first 30 days
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight max-w-3xl"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-first-30-title"
        >
          What actually happens{" "}
          <span className="text-brass italic">after you sign.</span>
        </h2>
        <p className="mt-4 font-sans text-ink-muted max-w-2xl leading-relaxed">
          No mystery, no drip of empty status updates. Here is the exact rhythm your dedicated team runs so you know what you are buying.
        </p>

        <ol className="mt-14 relative">
          {/* Vertical connector line */}
          <span
            aria-hidden
            className="absolute left-5 md:left-6 top-2 bottom-2 w-px"
            style={{
              background:
                "linear-gradient(180deg, rgba(201,169,97,0.55) 0%, rgba(201,169,97,0.15) 100%)",
            }}
          />

          {phases.map((p, i) => (
            <motion.li
              key={p.window}
              initial={{ opacity: 0, x: -12 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.35 }}
              transition={{ duration: 0.55, delay: i * 0.08, ease: EASE }}
              className="relative pl-16 md:pl-20 pb-10 last:pb-0"
              data-testid={`gp-first-30-step-${i}`}
            >
              {/* Milestone dot */}
              <span
                className="absolute left-0 top-1 h-11 w-11 md:h-12 md:w-12 rounded-full border border-brass/50 bg-[#0a0a0a] grid place-items-center text-brass"
                style={{
                  boxShadow:
                    "0 0 0 4px rgba(10,10,10,1), 0 0 30px -8px rgba(201,169,97,0.55)",
                }}
                aria-hidden
              >
                <p.icon className="h-4 w-4 md:h-5 md:w-5" />
              </span>

              <div className="rounded-2xl border border-brass/20 bg-black/40 p-6 md:p-7 hover:border-brass/45 transition-colors duration-500">
                <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
                  {p.window}
                </p>
                <h3 className="mt-2 font-serif text-2xl text-ink leading-tight">
                  {p.title}
                </h3>
                <p className="mt-3 font-sans text-sm md:text-base text-ink-muted leading-relaxed">
                  {p.body}
                </p>
              </div>
            </motion.li>
          ))}
        </ol>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - Who this is for                                           */
/* ================================================================== */
function WhoFor() {
  const fit = [
    "Founder-led coaching, consulting or recruiting practice",
    "You sell $10,000+ services to real humans",
    "Revenue between $30,000 and $500,000 per month",
    "You already know your ICP, or want help sharpening it",
    "You can show up prepared for the meetings we book",
  ];
  const notFit = [
    "You need volume-first cold email at $5 per lead",
    "You sell a $99 SaaS to a browser tab",
    "You cannot commit thirty minutes a week to review our work",
  ];
  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10"
      data-testid="gp-who"
    >
      <div className="max-w-7xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          Who this is built for
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight max-w-3xl"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-who-title"
        >
          Straight talk about fit.
        </h2>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div
            className="rounded-2xl border border-brass/45 bg-[#0a0a0a] p-7"
            data-testid="gp-who-fit"
          >
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
              Fit
            </p>
            <h3 className="mt-3 font-serif text-2xl text-ink leading-tight">
              This is for you if
            </h3>
            <ul className="mt-5 space-y-3 font-sans text-sm text-ink leading-relaxed">
              {fit.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <CheckCircle2 className="h-4 w-4 text-brass mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>

          <div
            className="rounded-2xl border border-ink/15 bg-[#0a0a0a] p-7"
            data-testid="gp-who-not-fit"
          >
            <p className="font-sans text-[10px] uppercase tracking-mega text-ink-muted">
              Not a fit
            </p>
            <h3 className="mt-3 font-serif text-2xl text-ink-muted leading-tight">
              Skip this if
            </h3>
            <ul className="mt-5 space-y-3 font-sans text-sm text-ink-muted leading-relaxed">
              {notFit.map((f) => (
                <li key={f} className="flex items-start gap-3">
                  <X className="h-4 w-4 text-ink-muted mt-0.5 shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - What the system is built to deliver (no fake proof)       */
/* ================================================================== */
function Capabilities() {
  const specs = [
    { n: "100-1000", label: "named-prospect videos per month, per client. More on a custom plan." },
    { n: "10-50+", label: "qualified meetings per month engineered into each tier" },
    { n: "<48h", label: "before your dedicated team starts building your ICP list, script and first videos" },
    { n: "<2h", label: "reply response time on every inbound during business hours" },
  ];
  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-black"
      data-testid="gp-capabilities"
    >
      <div className="max-w-7xl mx-auto">
        <div className="flex items-end justify-between flex-wrap gap-6">
          <div>
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
              What the system is engineered to deliver
            </p>
            <h2
              className="mt-3 font-serif text-ink leading-tight max-w-3xl"
              style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
              data-testid="gp-capabilities-title"
            >
              Meetings.{" "}
              <span className="text-brass italic">
                And a brand that shows up before you do.
              </span>
            </h2>
          </div>
          <p className="font-sans text-xs uppercase tracking-mega text-ink-muted max-w-xs md:text-right">
            Engineering specs. Not client claims. Get Virtual Growth is a young practice.
          </p>
        </div>

        <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-4">
          {specs.map((s, i) => (
            <motion.div
              key={s.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.5 }}
              transition={{ duration: 0.5, delay: i * 0.08, ease: EASE }}
              className="rounded-2xl border border-brass/25 bg-[#0a0a0a] p-6 md:p-7"
              data-testid={`gp-spec-${i}`}
            >
              <p
                className="font-serif text-brass leading-none"
                style={{ fontSize: "clamp(2.2rem, 4.4vw, 3.6rem)" }}
              >
                {s.n}
              </p>
              <p className="mt-3 font-sans text-xs md:text-sm text-ink-muted leading-relaxed">
                {s.label}
              </p>
            </motion.div>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="rounded-2xl border border-brass/25 bg-[#0a0a0a] p-6 md:p-7">
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
              Meetings
            </p>
            <p className="mt-3 font-serif text-xl text-ink leading-snug">
              Named calls with real decision-makers. Not lead lists. Not clicks. Meetings.
            </p>
          </div>
          <div className="rounded-2xl border border-brass/25 bg-[#0a0a0a] p-6 md:p-7">
            <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
              Reputation
            </p>
            <p className="mt-3 font-serif text-xl text-ink leading-snug">
              Every prospect sees a founder who took thirty seconds for them. Even the ones who never book compound your brand.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - Pricing (4 tiers, bundled)                                */
/* ================================================================== */
function Pricing({ onBook, onSelectTier }) {
  const tiers = [
    {
      id: "pilot",
      name: "Pilot",
      subtitle: "We share the risk",
      setup: "$2,000",
      monthly: "$400",
      cadence: "per booked meeting",
      description:
        "We believe the system works. So we price it that way. You pay setup and only when a real meeting lands on your calendar.",
      features: [
        "100-200 named-prospect videos to start",
        "You pay per booked meeting",
        "Rolling monthly, cancel any time",
        "Ideal to prove fit before scaling",
      ],
      cta: "Start with a Pilot",
      highlight: false,
    },
    {
      id: "foundation",
      name: "Foundation",
      subtitle: "Predictable engine",
      setup: "$4,000",
      monthly: "$2,500",
      cadence: "per month, all-in",
      description:
        "Steady named-prospect flow. Sourcing, cleaning, video, sending, replies and booking bundled into one monthly.",
      features: [
        "200-500 named-prospect videos per month",
        "Weekly briefing digest",
        "Full reply management",
        "Meeting booking + prep notes",
      ],
      cta: "Choose Foundation",
      highlight: false,
    },
    {
      id: "growth",
      name: "Growth",
      subtitle: "Most Founders Pick This",
      setup: "$4,000",
      monthly: "$5,000",
      cadence: "per month, all-in",
      description:
        "Double the volume, faster iteration, deeper research per prospect. Built for founders ready to compound.",
      features: [
        "500-1000 named-prospect videos per month",
        "Bi-weekly optimisation calls",
        "Deep research per prospect",
        "Priority reply handling",
      ],
      cta: "Choose Growth",
      highlight: true,
    },
    {
      id: "omnichannel",
      name: "Omnichannel",
      subtitle: "Multi-channel domination",
      setup: "$4,000",
      monthly: "$8,000",
      cadence: "per month, all-in",
      description:
        "Video across LinkedIn, email and WhatsApp. Managed as one campaign. For firms ready to be everywhere their buyers are.",
      features: [
        "500+ named-prospect videos per month",
        "LinkedIn, email, WhatsApp",
        "Let Krishna represent your brand (optional)",
        "Weekly strategy call with Krishna",
        "Custom volume available beyond 1000 videos/month",
      ],
      cta: "Choose Omnichannel",
      highlight: false,
    },
  ];

  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10"
      data-testid="gp-pricing"
    >
      <div className="max-w-7xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          Pricing . honest, tiered, no hidden fees
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight max-w-3xl"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-pricing-title"
        >
          Four ways to work with us.{" "}
          <span className="text-brass italic">
            One of them is right for you.
          </span>
        </h2>
        <p className="mt-4 font-sans text-ink-muted max-w-2xl leading-relaxed">
          Every tier includes end-to-end delivery. No add-on fees for prospecting, no per-video charge, no seat licences. What you see is what you pay.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {tiers.map((t, i) => (
            <motion.div
              key={t.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.06, ease: EASE }}
              className={`relative rounded-2xl p-6 md:p-7 flex flex-col ${
                t.highlight
                  ? "border-2 border-brass bg-[#0d0b06]"
                  : "border border-brass/25 bg-[#0a0a0a]"
              }`}
              style={
                t.highlight
                  ? {
                      boxShadow:
                        "0 30px 90px -30px rgba(201,169,97,0.35), inset 0 1px 0 rgba(201,169,97,0.15)",
                    }
                  : {}
              }
              data-testid={`gp-tier-${t.id}`}
            >
              {t.highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-brass text-bg px-3 py-1 font-sans text-[9px] uppercase tracking-mega font-semibold">
                  {t.subtitle}
                </span>
              )}

              <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
                {!t.highlight && t.subtitle}
                {t.highlight && "\u00A0"}
              </p>
              <h3 className="mt-2 font-serif text-3xl text-ink leading-tight">
                {t.name}
              </h3>

              <div className="mt-5 border-t border-brass/15 pt-5">
                <p className="font-sans text-[10px] uppercase tracking-mega text-ink-muted">
                  Setup
                </p>
                <p className="font-serif text-2xl text-ink">{t.setup}</p>
                <p className="mt-4 font-sans text-[10px] uppercase tracking-mega text-ink-muted">
                  Then
                </p>
                <p className="font-serif text-4xl text-brass leading-none">
                  {t.monthly}
                </p>
                <p className="mt-1 font-sans text-xs text-ink-muted">
                  {t.cadence}
                </p>
              </div>

              <p className="mt-5 font-sans text-sm text-ink-muted leading-relaxed">
                {t.description}
              </p>

              <ul className="mt-5 space-y-2 font-sans text-sm text-ink leading-relaxed">
                {t.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="h-3.5 w-3.5 text-brass mt-0.5 shrink-0" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              <button
                type="button"
                onClick={() => {
                  onSelectTier?.(t.name);
                  onBook();
                }}
                data-testid={`gp-tier-cta-${t.id}`}
                className={`mt-6 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500 ${
                  t.highlight
                    ? "bg-brass text-bg hover:bg-brass/85"
                    : "border border-brass/40 text-ink hover:border-brass hover:text-brass"
                }`}
              >
                {t.cta}
                <ArrowUpRight className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          ))}
        </div>

        <p className="mt-10 font-serif italic text-ink-muted text-lg leading-snug max-w-2xl">
          Not sure which tier fits? Book a Pilot. If the system clearly works for your firm, we move you up together.
        </p>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - FAQ                                                       */
/* ================================================================== */
function Faq() {
  const [open, setOpen] = useState(-1);
  return (
    <section
      className="relative py-16 md:py-24 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-black"
      data-testid="gp-faq"
    >
      <div className="max-w-4xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          Straight answers
        </p>
        <h2
          className="mt-3 font-serif text-ink leading-tight"
          style={{ fontSize: "clamp(1.9rem, 3.6vw, 3.1rem)" }}
          data-testid="gp-faq-title"
        >
          The questions founders actually ask.
        </h2>

        <div className="mt-10 divide-y divide-brass/15 border-y border-brass/15">
          {CORE_FAQS.map((f, i) => {
            const isOpen = open === i;
            return (
              <div key={f.q} data-testid={`gp-faq-item-${i}`}>
                <button
                  type="button"
                  onClick={() => setOpen(isOpen ? -1 : i)}
                  aria-expanded={isOpen}
                  className="w-full text-left py-5 flex items-start justify-between gap-6"
                >
                  <span className="font-serif text-lg md:text-xl text-ink leading-snug">
                    {f.q}
                  </span>
                  <ChevronDown
                    className={`h-5 w-5 text-brass shrink-0 mt-1 transition-transform duration-300 ${
                      isOpen ? "rotate-180" : ""
                    }`}
                  />
                </button>
                {isOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -6 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: EASE }}
                    className="pb-6 pr-8 font-sans text-ink-muted text-sm md:text-base leading-relaxed"
                  >
                    {f.a}
                  </motion.div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - A Note from the Founder (trust unlock before pricing)     */
/* ================================================================== */
function FounderNote() {
  return (
    <section
      className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-black overflow-hidden"
      data-testid="gp-founder-note"
    >
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 60% 45% at 30% 40%, rgba(201,169,97,0.10) 0%, transparent 70%)",
        }}
      />
      <div className="relative max-w-6xl mx-auto grid md:grid-cols-[220px_1fr] lg:grid-cols-[260px_1fr] gap-10 md:gap-14 lg:gap-16 items-start">
        {/* Founder card - fills the column with real credentials */}
        <motion.aside
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.7, ease: EASE }}
          className="shrink-0 mx-auto md:mx-0 w-40 md:w-full text-center md:text-left"
          data-testid="gp-founder-card"
        >
          <div
            className="relative w-40 h-40 md:w-full md:h-auto md:aspect-square mx-auto rounded-full md:rounded-[2px] overflow-hidden border border-brass/25"
            style={{ boxShadow: "0 30px 80px -30px rgba(201,169,97,0.35)" }}
          >
            <picture>
              <source srcSet="/founder/krishna-portrait-2026.webp" type="image/webp" />
              <img
                src="/founder/krishna-portrait-2026.jpg"
                alt="Krishna S, Founder of Get Virtual Growth"
                loading="lazy"
                decoding="async"
                className="w-full h-full object-cover"
                data-testid="gp-founder-portrait"
              />
            </picture>
          </div>

          {/* Credentials under the portrait - fills the empty column */}
          <div className="mt-6">
            <p
              className="font-serif italic text-ink"
              style={{ fontSize: "clamp(1.05rem, 1.3vw, 1.2rem)" }}
              data-testid="gp-founder-signature"
            >
              Krishna S.
            </p>
            <p className="mt-1 font-sans text-[10px] uppercase tracking-mega text-brass">
              Founder, Get Virtual Growth
            </p>

            <div className="mt-5 pt-5 border-t border-brass/15 space-y-3 font-sans text-[11px] uppercase tracking-[0.22em] text-ink-muted">
              <p className="flex items-center gap-2 justify-center md:justify-start" data-testid="gp-founder-location">
                <span className="hidden md:inline-block w-1 h-1 rounded-full bg-brass shrink-0" aria-hidden />
                Based in Dubai . Working globally
              </p>
              <a
                href="https://www.linkedin.com/in/fieldnotesbykrishna"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 hover:text-brass transition-colors duration-500"
                style={{ transitionTimingFunction: "cubic-bezier(0.2,0.8,0.2,1)" }}
                data-testid="gp-founder-linkedin"
              >
                <span className="hidden md:inline-block w-1 h-1 rounded-full bg-brass shrink-0" aria-hidden />
                Connect on LinkedIn
              </a>
            </div>
          </div>
        </motion.aside>

        {/* Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, ease: EASE, delay: 0.1 }}
        >
          <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
            A note from the founder
          </p>
          <h2
            className="mt-4 font-serif text-ink leading-[1.1]"
            style={{ fontSize: "clamp(1.6rem, 3.6vw, 2.6rem)", letterSpacing: "-0.01em" }}
            data-testid="gp-founder-note-title"
          >
            What if your business could grow{" "}
            <span className="italic text-brass">without growing the cost and complexity</span>{" "}
            that comes with it?
          </h2>

          <div
            className="mt-8 space-y-5 font-serif text-ink-muted leading-relaxed max-w-2xl"
            style={{ fontSize: "clamp(1rem, 1.25vw, 1.15rem)" }}
            data-testid="gp-founder-note-body"
          >
            <p>That is the problem I work on.</p>
            <p>
              I work alongside founders as a Growth Partner, building the systems, processes and technology that help a business grow more efficiently, reduce unnecessary cost and move faster.
            </p>
            <p>
              My team and I work around your existing people and strengths. We do not come in to replace good people. We build what is missing, improve what is slowing you down and create systems that allow your team to do better work.
            </p>
            <p className="text-ink">
              You are not buying another software tool. You are gaining a Growth Partner, backed by a team that never sleeps.
            </p>
            <p>
              Your team focuses on the product, service and customers.{" "}
              <span className="text-brass italic">
                We focus on building the engine that helps the business grow.
              </span>
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Section - The Moat (value stack before close)                       */
/* ================================================================== */
function Moat() {
  const stack = [
    { label: "We build your brand.", tone: "muted" },
    { label: "We build your reputation.", tone: "muted" },
    { label: "We bring meetings.", tone: "ink" },
    { label: "We bring sales.", tone: "brass" },
    { label: "We bring revenue.", tone: "brass" },
  ];
  return (
    <section
      className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-black overflow-hidden"
      data-testid="gp-moat"
    >
      <span
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 70% 50% at 50% 100%, rgba(201,169,97,0.14) 0%, transparent 70%)",
        }}
      />
      <div className="relative max-w-5xl mx-auto">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          The moat
        </p>
        <h2
          className="mt-4 font-serif text-ink leading-[1.05]"
          style={{ fontSize: "clamp(2rem, 5vw, 4rem)", letterSpacing: "-0.01em" }}
          data-testid="gp-moat-title"
        >
          Ten to a hundred calendar meetings a month.{" "}
          <span className="text-brass italic">Clean. Qualified. Named.</span>{" "}
          That is what we bring.
        </h2>
        <p
          className="mt-6 font-sans text-ink-muted leading-relaxed max-w-2xl"
          style={{ fontSize: "clamp(1rem, 1.3vw, 1.1rem)" }}
        >
          Meetings are the moment. Everything else you have been chasing lives downstream of them. So we built the engine that puts them on your calendar without diluting your voice, your value, or your price.
        </p>

        <ol className="mt-14 space-y-2 md:space-y-3" data-testid="gp-moat-stack">
          {stack.map((s, i) => (
            <motion.li
              key={s.label}
              initial={{ opacity: 0, x: -18 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.55, delay: i * 0.09, ease: EASE }}
              className="flex items-baseline gap-4 md:gap-6"
              data-testid={`gp-moat-line-${i}`}
            >
              <span
                className="font-mono text-[10px] md:text-xs uppercase tracking-mega shrink-0"
                style={{
                  color: s.tone === "brass" ? "#C9A961" : "rgba(255,255,255,0.35)",
                  width: "2.4rem",
                }}
              >
                0{i + 1}
              </span>
              <span
                className="font-serif leading-tight"
                style={{
                  fontSize: "clamp(1.6rem, 3.6vw, 3.2rem)",
                  color:
                    s.tone === "brass"
                      ? "#C9A961"
                      : s.tone === "ink"
                      ? "#F5F1E8"
                      : "rgba(245,241,232,0.55)",
                  fontStyle: s.tone === "brass" ? "italic" : "normal",
                  fontWeight: s.tone === "brass" ? 500 : 400,
                }}
              >
                {s.label}
              </span>
            </motion.li>
          ))}
        </ol>

        <p
          className="mt-14 font-serif italic text-ink leading-snug max-w-2xl"
          style={{ fontSize: "clamp(1.1rem, 1.6vw, 1.4rem)" }}
        >
          Without diluting your brand. Without discounting your value. That is the moat.
        </p>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Exit Intent - fires the Moat value stack when the buyer tries      */
/*  to leave the page (mouse to top, mobile back-scroll, or idle)      */
/* ================================================================== */
const EXIT_KEY = "gvg_gp_exit_shown";

function ExitIntentMoat({ open, onClose, onBook }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) {
      window.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    }
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  if (!open) return null;

  const stack = [
    { n: "01", label: "We build your brand.", tone: "muted" },
    { n: "02", label: "We build your reputation.", tone: "muted" },
    { n: "03", label: "We bring meetings.", tone: "ink" },
    { n: "04", label: "We bring sales.", tone: "brass" },
    { n: "05", label: "We bring revenue.", tone: "brass" },
  ];

  return (
    <div
      className="fixed inset-0 z-[110] flex items-center justify-center px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gp-exit-title"
      data-testid="gp-exit-intent"
    >
      <button
        type="button"
        onClick={onClose}
        aria-label="Close"
        className="absolute inset-0 bg-black/90 backdrop-blur-md"
        data-testid="gp-exit-backdrop"
      />
      <motion.div
        initial={{ opacity: 0, y: 16, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.5, ease: EASE }}
        className="relative w-full max-w-lg rounded-3xl border border-brass/40 bg-[#0a0a0a] p-8 md:p-10 overflow-hidden"
        style={{
          boxShadow:
            "0 40px 120px -30px rgba(0,0,0,0.95), 0 0 80px rgba(201,169,97,0.18)",
        }}
      >
        <span
          aria-hidden
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse 80% 60% at 50% 100%, rgba(201,169,97,0.12) 0%, transparent 70%)",
          }}
        />
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute top-5 right-5 h-8 w-8 rounded-full text-ink-muted hover:text-brass transition-colors flex items-center justify-center"
          data-testid="gp-exit-close"
        >
          <X className="h-4 w-4" />
        </button>

        <div className="relative">
          <p
            className="font-sans text-[10px] uppercase tracking-mega text-brass"
            data-testid="gp-exit-eyebrow"
          >
            Before you go
          </p>
          <h3
            id="gp-exit-title"
            className="mt-3 font-serif text-ink leading-tight"
            style={{ fontSize: "clamp(1.6rem, 3.4vw, 2.4rem)" }}
            data-testid="gp-exit-title"
          >
            Ten to a hundred meetings.{" "}
            <span className="text-brass italic">Every month.</span>{" "}
            That is the moat.
          </h3>

          <ol className="mt-6 space-y-1.5" data-testid="gp-exit-stack">
            {stack.map((s) => (
              <li key={s.n} className="flex items-baseline gap-3">
                <span
                  className="font-mono text-[9px] uppercase tracking-mega shrink-0"
                  style={{
                    color:
                      s.tone === "brass" ? "#C9A961" : "rgba(255,255,255,0.30)",
                    width: "1.6rem",
                  }}
                >
                  {s.n}
                </span>
                <span
                  className="font-serif leading-tight"
                  style={{
                    fontSize: "clamp(1.05rem, 2vw, 1.35rem)",
                    color:
                      s.tone === "brass"
                        ? "#C9A961"
                        : s.tone === "ink"
                        ? "#F5F1E8"
                        : "rgba(245,241,232,0.55)",
                    fontStyle: s.tone === "brass" ? "italic" : "normal",
                    fontWeight: s.tone === "brass" ? 500 : 400,
                  }}
                >
                  {s.label}
                </span>
              </li>
            ))}
          </ol>

          <p className="mt-6 font-serif italic text-ink-muted leading-snug">
            Without diluting your brand. Without discounting your value.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => {
                onClose();
                onBook();
              }}
              data-testid="gp-exit-book"
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-6 py-3.5 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500"
            >
              Start with a Pilot
              <ArrowUpRight className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="gp-exit-later"
              className="rounded-full border border-brass/25 hover:border-brass/60 px-5 py-3 font-sans text-[11px] uppercase tracking-mega text-ink-muted hover:text-ink transition-colors duration-500"
            >
              Not today
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

function useExitIntent(enabled) {
  const [triggered, setTriggered] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    if (sessionStorage.getItem(EXIT_KEY) === "1") return;

    // Give the buyer 15 seconds to actually see the page before firing
    let armed = false;
    const armTimer = setTimeout(() => {
      armed = true;
    }, 15000);

    // Desktop: mouse leaves the top of the viewport
    const onMouseOut = (e) => {
      if (!armed) return;
      if (e.relatedTarget || e.toElement) return;
      if (e.clientY > 10) return;
      fire();
    };

    // Mobile: fast upward flick after user has scrolled deep into the page
    let lastY = window.scrollY;
    let lastT = performance.now();
    const onScroll = () => {
      if (!armed) return;
      const y = window.scrollY;
      const t = performance.now();
      const dy = lastY - y;
      const dt = t - lastT;
      const scrolledFarEnough = y > window.innerHeight * 1.5;
      const flickedUp = dy > 220 && dt < 400;
      if (scrolledFarEnough && flickedUp) fire();
      lastY = y;
      lastT = t;
    };

    // Tab-switch fallback: if the user hides the tab after arming, fire on return
    const onVisibility = () => {
      if (document.visibilityState === "hidden" && armed) fire();
    };

    const fire = () => {
      if (sessionStorage.getItem(EXIT_KEY) === "1") return;
      sessionStorage.setItem(EXIT_KEY, "1");
      setTriggered(true);
    };

    document.addEventListener("mouseout", onMouseOut);
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      clearTimeout(armTimer);
      document.removeEventListener("mouseout", onMouseOut);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [enabled]);

  return [triggered, setTriggered];
}

/* ================================================================== */
/*  Section - Close                                                     */
/* ================================================================== */
function Close({ onBook }) {
  return (
    <section
      className="relative py-20 md:py-28 px-6 md:px-10 lg:px-16 border-t border-brass/10 bg-gradient-to-b from-black via-[#08070a] to-black"
      data-testid="gp-close"
    >
      <div className="max-w-4xl mx-auto text-center">
        <p className="font-sans text-[10px] uppercase tracking-mega text-brass">
          The next move
        </p>
        <h2
          className="mt-4 font-serif text-ink leading-tight"
          style={{ fontSize: "clamp(2rem, 4.4vw, 3.6rem)" }}
          data-testid="gp-close-title"
        >
          Start with a Pilot.{" "}
          <span className="text-brass italic">We share the risk.</span>
        </h2>
        <p
          className="mt-5 font-sans text-ink-muted leading-relaxed max-w-xl mx-auto"
          style={{ fontSize: "clamp(1rem, 1.3vw, 1.1rem)" }}
        >
          $2,000 setup and $400 per booked meeting. That is it. We believe the system works for founder-led firms. So we price it that way.
        </p>

        <button
          type="button"
          onClick={onBook}
          data-testid="gp-close-book"
          className="mt-9 inline-flex items-center gap-2 rounded-full bg-brass text-bg hover:bg-brass/85 px-7 py-4 font-sans text-[11px] uppercase tracking-mega font-semibold transition-colors duration-500"
        >
          Start with a Pilot
          <ArrowRight className="h-3.5 w-3.5" />
        </button>

        <p className="mt-5 font-sans text-[10px] uppercase tracking-mega text-ink-muted">
          48-hour start . Founder led . Get Virtual Growth
        </p>
      </div>
    </section>
  );
}

/* ================================================================== */
/*  Page                                                                */
/* ================================================================== */
export default function GrowthPartnership() {
  usePageTitle("Growth Partnership . Get Virtual Growth");
  const [modalOpen, setModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [preselectedTier, setPreselectedTier] = useState("");

  const openBook = () => setModalOpen(true);

  // Exit-intent: only arm while the buyer has not booked and the booking
  // modal is not already open on screen.
  const [exitOpen, setExitOpen] = useExitIntent(!submitted && !modalOpen);

  const canonical = `${ENTITY.url}/growth-partnership`;
  const title =
    "Growth Partnership . Managed video customer acquisition . Get Virtual Growth";
  const description =
    "Managed personalised video outreach for founder-led firms. Sourcing, ICP cleaning, video, delivery, replies and meeting booking - all done for you. Start with a Pilot from $2,000 setup + $400 per booked meeting.";
  const jsonLd = [
    organizationSchema(ENTITY),
    serviceSchema(ENTITY, SERVICE),
    faqPageSchema(CORE_FAQS),
    breadcrumbSchema([
      { name: "Home", url: ENTITY.url },
      { name: "Growth Partnership", url: canonical },
    ]),
  ];

  return (
    <div
      className="min-h-screen bg-bg text-ink relative overflow-x-hidden gvg-typography"
      data-testid="growth-partnership-page"
    >
      <PremiumBackdrop />
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <ScrollProgress />

      <Hero onBook={openBook} />
      <WhatWeDo />
      <WhenWhereHow />
      <FirstThirtyDays />
      <WhoFor />
      <Capabilities />
      <FounderNote />
      <Pricing onBook={openBook} onSelectTier={setPreselectedTier} />
      <Faq />
      <Moat />
      <Close onBook={openBook} />

      <MobileStickyCta onBook={openBook} hide={submitted} />
      <CallModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        onSubmitted={() => setSubmitted(true)}
        preselectedTier={preselectedTier}
      />
      <ExitIntentMoat
        open={exitOpen}
        onClose={() => setExitOpen(false)}
        onBook={openBook}
      />
    </div>
  );
}
